import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  type ApiError,
  type GlobalSearchResult,
  type RecentlyViewedResult,
} from '@whinerp/client';
import { CapabilityUnavailable, Icon, Skeleton } from '@whinerp/ui';
import { useT } from '../app/i18n.js';
import { useEntitlement } from '../app/session.js';
import { SearchResults } from '../screens/shared/search-results.js';
import { recordHref } from '../screens/shared/record-href.js';
import { Overlay } from './Overlay.js';

/**
 * Global search (F-020).
 *
 * The scope is the enabled objects and nothing else - the same entitlement set
 * the navigation reads, which is what makes switching a module off remove it
 * from search in the same request cycle rather than leaving its records
 * findable.
 *
 * The search backend is not built in this deployment yet, so this says so
 * plainly rather than rendering a box that never returns anything. A search
 * field that silently finds nothing is worse than one that explains itself.
 */
export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const t = useT();
  const [query, setQuery] = useState('');
  const search = useEntitlement('F-020');
  const term = query.trim();

  const results = useQuery<GlobalSearchResult, ApiError>({
    queryKey: ['search', term],
    queryFn: () => api.globalSearch({ query: { q: term } }),
    enabled: open && search.state === 'on' && term.length > 0,
  });

  /*
   * Recents when the box is focused and empty.
   *
   * Most searches are re-finds: somebody looking for the account they had open
   * twenty minutes ago is navigating, not searching, and a box that stays
   * blank until you type makes them retype a name they already know.
   */
  const recents = useQuery<RecentlyViewedResult, ApiError>({
    queryKey: ['recents'],
    queryFn: () => api.recentlyViewed({ query: {} }),
    enabled: open && search.state === 'on' && term.length === 0,
  });

  return (
    <Overlay open={open} onClose={onClose} label={t('topbar.search')}>
      <div data-component="GlobalSearch" className="flex flex-col">
        <div className="flex items-center gap-2 border-b border-b-rule px-4 py-3">
          <Icon name="MagnifyingGlass" decorative className="text-ink-400" />
          <input
            type="search"
            data-search-input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('topbar.searchPlaceholder')}
            aria-label={t('topbar.search')}
            className="h-input w-full border-0 bg-transparent text-body text-ink outline-none placeholder:text-ink-300"
          />
        </div>

        <div className="p-4">
          {search.state !== 'on' ? (
            <CapabilityUnavailable
              kind="module-off"
              capability={t('topbar.search')}
              title={t('capability.moduleOff.title')}
              body={t('capability.moduleOff.body', { values: { capability: t('topbar.search') } })}
            />
          ) : term === '' ? (
            recents.isPending ? (
              <Skeleton className="h-block-sm w-full" />
            ) : (recents.data?.recents.length ?? 0) === 0 ? (
              <p className="text-dense text-ink-meta">{t('search.hint')}</p>
            ) : (
              <div data-search-recents className="flex flex-col gap-1">
                <p className="text-micro uppercase tracking-label text-ink-label">
                  {t('search.recents')}
                </p>
                <ul className="flex flex-col">
                  {recents.data?.recents.map((recent) => (
                    <li key={`${recent.objectKey}:${recent.recordId}`}>
                      <Link
                        to={recordHref(recent.objectKey, recent.recordId)}
                        data-search-recent={recent.recordId}
                        onClick={onClose}
                        className="block rounded-control px-2 py-1.5 text-body text-ink hover:bg-sunken"
                      >
                        {recent.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ) : results.isPending ? (
            <Skeleton className="h-block-sm w-full" />
          ) : (results.data?.groups.length ?? 0) === 0 ? (
            <p data-search-none className="text-dense text-ink-meta">
              {t('search.none', { values: { term } })}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <SearchResults groups={results.data?.groups ?? []} onNavigate={onClose} />
              <Link
                to={`/search?q=${encodeURIComponent(term)}`}
                data-search-see-all
                onClick={onClose}
                className="text-dense text-bronze-text underline decoration-bronze-rule underline-offset-4"
              >
                {t('search.seeAll')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </Overlay>
  );
}
